import { app } from "@microsoft/teams-js";
import { GraphAPIPermissionScopes } from "@teams/types";
import { UserDTO } from "@teams/types/api";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { AlertError } from "../components/AlertError";
import { TeamsLoader } from "../components/TeamsLoader";
import { isError } from "../services/ChannelApi";
import { userApi } from "../services/UserApi";
import { useTeamsContext } from "../state/TeamsContextProvider";

export type UserInfoContext = {
  user: UserDTO;
  npsEligibility?: boolean;
  setNpsEligibility: (eligibility: boolean) => void;
  graphPermissionsConsented?: GraphAPIPermissionScopes[];
  updateGraphPermissions: (consented: GraphAPIPermissionScopes[]) => Promise<void | Error>;
  loadUser: (supressError?: boolean) => Promise<UserDTO | undefined>;
};

const UserContext = createContext<UserInfoContext>({} as any);

export const UserInfoProvider: React.FC<{ defaultUser?: UserDTO }> = ({
  defaultUser,
  children,
}) => {
  const { teamsContext } = useTeamsContext();
  const [user, setUser] = useState<UserDTO | undefined>(defaultUser);
  const [npsEligibility, setNpsEligibility] = useState<boolean | undefined>(undefined);
  const [graphPermissionsConsented, setGraphPermissionsConsented] =
    useState<GraphAPIPermissionScopes[]>();
  const [error, setError] = useState<Error>();

  const loadUser = useCallback(
    async (supressError?: boolean) => {
      setError(undefined);

      const userInfo = await userApi.getUser(teamsContext);
      if (isError(userInfo)) {
        if (!supressError) {
          setError(userInfo);
        }
        return;
      }
      setUser(userInfo);
      return userInfo;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamsContext]
  );

  const loadNpsEligibility = useCallback(async () => {
    const eligibility = await getNpsEligibility(teamsContext);
    setNpsEligibility(eligibility);
  }, [teamsContext]);

  const loadGraphPermissionsConsent = useCallback(async () => {
    const consentedPermissions = await getGraphPermissionConsentStatus(teamsContext);
    if (consentedPermissions) {
      setGraphPermissionsConsented(consentedPermissions);
    }
  }, [teamsContext]);

  const updateGraphPermissions = useCallback(
    async (newPermissions: GraphAPIPermissionScopes[]) => {
      const result = await userApi.updateUserGraphPermissionConsent(teamsContext, newPermissions);
      if (!isError(result)) {
        setGraphPermissionsConsented(newPermissions);
      }
      return result;
    },
    [teamsContext]
  );

  useEffect(() => {
    if (teamsContext && user) {
      return;
    }
    loadUser();
  }, [loadUser, teamsContext, user]);

  useEffect(() => {
    if (npsEligibility === undefined && user) {
      loadNpsEligibility();
    }
  }, [loadNpsEligibility, npsEligibility, user]);

  useEffect(() => {
    if (graphPermissionsConsented === undefined && user) {
      loadGraphPermissionsConsent();
    }
  }, [loadGraphPermissionsConsent, graphPermissionsConsented, user]);

  if (error) {
    return (
      <AlertError
        prependMessage="Sorry, there was a problem loading your account:"
        error={error}
        onClick={loadUser}
      />
    );
  }

  return (
    <TeamsLoader isLoading={!user} loadingMessage="Loading account...">
      <UserContext.Provider
        value={{
          user: user as UserDTO,
          npsEligibility: npsEligibility,
          setNpsEligibility: setNpsEligibility,
          graphPermissionsConsented,
          updateGraphPermissions,
          loadUser,
        }}
      >
        {children}
      </UserContext.Provider>
    </TeamsLoader>
  );
};

export const useUserInfo = () => useContext(UserContext);

const getNpsEligibility = async (teamsContext: app.Context) => {
  const npsEligibility = await userApi.getNpsEligibility(teamsContext);
  if (isError(npsEligibility)) {
    console.error(npsEligibility);
    return false;
  }
  return npsEligibility.isEligible;
};

const getGraphPermissionConsentStatus = async (teamsContext: app.Context) => {
  const consent = await userApi.getGraphPermissionConsentStatus(teamsContext);
  if (isError(consent)) {
    console.error(consent);
    return undefined;
  }
  return consent.consented;
};
